import { AxiosError } from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { IApplicantData } from '../../../models/dashboard/jobs/singleJobApplicants.model';
import {
  ErrorHttpResponse,
  HttpConfig,
  SuccessHttpResponse,
} from '../../../models/https';
import useHttps from '../../useHttps';
import { useAppDisPatch } from '../../../redux/store';
import { setJobPermissions } from '../../../redux/slices/jobPermissionsSlice';

const useJobPermissions = (jobId: string, run: boolean = false) => {
  const request = useHttps();
  const dispatch = useAppDisPatch();
  const [loading, setLoading] = useState(false);

  const onSuccess = useCallback(
    ({ data, message }: SuccessHttpResponse<IApplicantData[]>) => {
      // Business logic: if there is at least one applicant and not all are rejected,
      // then disable editing/deleting. Otherwise, allow editing/deleting.
      const hasApplicants = data.length > 0;
      const allRejected =
        hasApplicants &&
        data.every((applicant) => applicant.status === 'REJECTED');
      const canEdit = !hasApplicants || allRejected;
      const canDelete = !hasApplicants || allRejected;
      dispatch(setJobPermissions({ jobId, canEdit, canDelete }));
    },
    [jobId, dispatch]
  );

  const onError = useCallback(
    (err: AxiosError<ErrorHttpResponse>) => {
      if (
        err.response?.status === 404 &&
        err.response?.data?.message === 'No application found'
      ) {
        /**
         * TODO:
         * 1. Remove this special-case error handling once the backend endpoint `/jobs/{jobId}/applicants`
         *    returns an empty array instead of a 404 error when no applicants are found.
         * 2. Once fixed, let the error propagate normally instead of handling it here.
         */
        onSuccess({
          status: 'success',
          data: [],
          message: 'No application found',
        });
        setLoading(false);
        return;
      }
      console.error(
        err?.message ?? 'Something went wrong while fetching permissions'
      );
    },
    [onSuccess]
  );

  const getPermissions = useCallback(() => {
    const url: HttpConfig = {
      url: `jobs-service/jobs/${jobId}/applicants`,
      method: 'get',
    };
    request(url, onSuccess, setLoading, onError);
  }, [jobId, request, onSuccess, onError]);

  useEffect(() => {
    if (run) {
      getPermissions();
    }
  }, [getPermissions, run]);

  return { loading };
};

export default useJobPermissions;
